import { datadogRum } from '@datadog/browser-rum';

export const SESSION_END_MARKER = '[session-end]';

export const SOCKET_RETRY_MARKER = '[socket-retry]';

export const SESSION_END_ACTION_NAME = 'session-end';

const SESSION_ID_PARAMETER_MESSAGE =
  'unrecognized configuration parameter "jwt.claims.session_id"';

export const SESSION_ERROR_MESSAGES: ReadonlyArray<string> = [
  'invalid input syntax for integer: ""',
  'unrecognized configuration parameter "jwt.claims.user_id"',
  SESSION_ID_PARAMETER_MESSAGE,
  'permission denied for relation charter_parties',
  'jwt expired',
];

export type SessionEndCause =
  | { kind: 'user-logout' }
  | { kind: 'other-tab-logout' }
  | { kind: 'session-error'; message: string }
  | { kind: 'rejected-request'; status: number }
  | { kind: 'socket-closed'; code: number; reason?: string }
  | { kind: 'token-expired' }
  | { kind: 'no-user-for-token' };

export type SessionEndErrorInput = {
  graphQLErrors?: ReadonlyArray<{
    message: string;
    path?: ReadonlyArray<string | number>;
  }>;
  networkError?: { message: string; statusCode?: number } | null;
};

const isSessionError = (error: {
  message: string;
  path?: ReadonlyArray<string | number>;
}): boolean =>
  SESSION_ERROR_MESSAGES.includes(error.message) &&
  (error.path?.at(0) !== 'currentUser' ||
    error.message !== SESSION_ID_PARAMETER_MESSAGE);

// Format owned by @apollo/client/link/subscriptions; a bump can reshape it.
const SOCKET_CLOSED_PATTERN = /Socket closed(?: with event (\d+)(?:\s+(.+))?)?/;

// The peer's close reason is never echoed; the code alone names the reason.
const CLOSE_REASONS: Readonly<Record<number, string>> = {
  1000: 'Normal Closure',
  1001: 'Going Away',
  1002: 'Protocol Error',
  1003: 'Unsupported Data',
  1006: 'Abnormal Closure',
  1011: 'Internal Error',
  1012: 'Service Restart',
  1013: 'Try Again Later',
  1014: 'Bad Gateway',
  4004: 'Bad Response',
  4005: 'Internal Client Error',
  4400: 'Bad Request',
  4401: 'Unauthorized',
  4403: 'Forbidden',
  4406: 'Subprotocol Not Acceptable',
  4408: 'Connection Initialisation Timeout',
  4409: 'Subscriber Already Exists',
  4429: 'Too Many Initialisation Requests',
  4499: 'Terminated',
  4500: 'Internal Server Error',
  4504: 'Connection Acknowledgement Timeout',
};

// graphql-ws re-establishes the connection after these, so the session lives
// through the gap: a lost network, our own keepalive close, and a server that
// is restarting or busy. Every other code names an authentication, protocol,
// or server-error close, which the retry would only repeat with the same
// rejected token.
const RETRIED_CLOSE_CODES: ReadonlySet<number> = new Set([
  1000, // Normal Closure
  1001, // Going Away
  1005, // No Status Received
  1006, // Abnormal Closure
  1012, // Service Restart
  1013, // Try Again Later
  1014, // Bad Gateway
  4408, // Connection Initialisation Timeout — the keepalive close we send
  4499, // Terminated
  4504, // Connection Acknowledgement Timeout
]);

type SocketClose = { code: number; reason?: string };

// A close the link reports without a CloseEvent carries no code, so it names
// no reason to end the session on.
const parseSocketClose = (message: string): SocketClose | null => {
  const match = SOCKET_CLOSED_PATTERN.exec(message);
  if (!match || match[1] === undefined) {
    return null;
  }
  const code = Number(match[1]);
  const reason = CLOSE_REASONS[code];
  return { code, ...(reason === undefined ? {} : { reason }) };
};

export const classifySessionEnd = (
  input: SessionEndErrorInput,
): SessionEndCause | null => {
  const graphQLError = input.graphQLErrors?.find(isSessionError);
  if (graphQLError) {
    return { kind: 'session-error', message: graphQLError.message };
  }

  const networkError = input.networkError;
  if (!networkError) {
    return null;
  }

  const status = networkError.statusCode;
  if (status === 401 || status === 403) {
    return { kind: 'rejected-request', status };
  }

  const socketClose = parseSocketClose(networkError.message);
  if (socketClose && !RETRIED_CLOSE_CODES.has(socketClose.code)) {
    return { kind: 'socket-closed', ...socketClose };
  }

  return null;
};

// Sentry's console capture treats any Error argument as an exception and
// matches ignoreErrors against its message, so records carry plain data only.
const describeError = (err: unknown): { message: string } => ({
  message: err instanceof Error ? err.message : String(err),
});

// Failures while a session is ending are diagnostics, not new errors.
export const reportSessionEndFailure = (step: string, err: unknown): void => {
  console.error(`${SESSION_END_MARKER} ${step} failed`, describeError(err));
};

// A close the client retries leaves the session open, so it is a diagnostic
// rather than an ending event: no cause is recorded and no user is signed out.
// Console capture collects warn and error only, so an info line stays a
// breadcrumb instead of opening an error-tracking issue. Returns whether the
// error was such a close and has been logged here.
export const reportRetriedClose = (
  networkError: { message: string } | null | undefined,
): boolean => {
  const close = networkError ? parseSocketClose(networkError.message) : null;
  if (!close || !RETRIED_CLOSE_CODES.has(close.code)) {
    return false;
  }
  console.info(`${SOCKET_RETRY_MARKER} socket closed`, close);
  return true;
};

let recorded = false;

// Every ending navigates away; later errors on the same page are fallout.
export const emitSessionEndCause = (cause: SessionEndCause): void => {
  if (recorded) return;
  recorded = true;
  const { kind, ...detail } = cause;
  const context = { cause: kind, ...detail };
  const detailText = Object.keys(detail).length
    ? ` ${JSON.stringify(detail)}`
    : '';
  console.error(`${SESSION_END_MARKER} ${kind}${detailText}`, context);
  datadogRum.addAction(SESSION_END_ACTION_NAME, context);
};
